import { Badge, Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import type {
  ConversationChannelType,
  CreatorHubConversationChannel,
} from '@fluenceur/types';
import { conversationMessageMaxCharacters, countMessageCharacters } from '@fluenceur/types';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import type { EditorState, LexicalEditor } from 'lexical';
import { $getRoot } from 'lexical';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExposeEditorInstancePlugin, SendOnEnterPlugin } from './conversation-editor-plugins';
import { SubmitConversationButton } from './SubmitConversationButton';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';

interface ConversationInputProps {
  onSendMessage: (content: string) => Promise<void>;
  isLoading?: boolean;
  channels: CreatorHubConversationChannel[];
  selectedChannelId: string | null;
  onChannelChange: (channelId: string) => void;
  /** Called as the draft changes while it holds text (the creator sees someone writing). */
  onTyping?: () => void;
}

export const getConversationMessageCharacterLimit = (channelType?: ConversationChannelType) =>
  conversationMessageMaxCharacters(channelType);

export const ConversationInput: React.FC<ConversationInputProps> = ({
  onSendMessage,
  isLoading = false,
  channels,
  selectedChannelId,
  onChannelChange,
  onTyping,
}) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [editor, setEditor] = useState<LexicalEditor | null>(null);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [charCount, setCharCount] = useState(0);
  const submissionInFlightRef = useRef(false);

  const selectedChannel = channels.find((channel) => channel.id === selectedChannelId) ?? null;
  const maxCharacters = getConversationMessageCharacterLimit(selectedChannel?.type);
  const isOverLimit = charCount > maxCharacters;
  const isNearLimit = charCount >= maxCharacters * 0.9 && !isOverLimit;

  // Theme configuration for Lexical
  const theme = {
    paragraph: 'editor-paragraph',
    text: {
      base: 'editor-text',
      bold: 'editor-text-bold',
      italic: 'editor-text-italic',
    },
  };

  // Initialize the editor
  const initialConfig = {
    namespace: 'ConversationEditor',
    theme,
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
  };

  const handleSubmit = useCallback(async () => {
    if (!editor || submissionInFlightRef.current) return;
    let msg = '';
    editor.getEditorState().read(() => {
      msg = $getRoot().getTextContent();
    });
    if (!msg.trim()) return;
    submissionInFlightRef.current = true;
    setSending(true);
    try {
      await onSendMessage(msg);
      editor.update(() => {
        $getRoot().clear();
      });
    } catch {
      // The mutation keeps the draft in place and surfaces translated feedback.
    } finally {
      submissionInFlightRef.current = false;
      setSending(false);
    }
  }, [onSendMessage, editor]);

  const handleEditorChange = useCallback(
    (editorState: EditorState, _editorInstance: LexicalEditor) => {
      editorState.read(() => {
        const text = $getRoot().getTextContent();
        setIsEditorEmpty(!text.trim());
        setCharCount(countMessageCharacters(text));
        if (text.trim()) onTyping?.();
      });
    },
    [onTyping]
  );

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="transparent"
      maxW="4xl"
      minH="80px"
      mx="auto"
      pos="relative"
      zIndex={100}
      rounded="2xl"
      shadow="2xl"
      w="full"
      p={4}
      transition="all 0.2s ease-in-out"
      _hover={{
        borderColor: 'light-gray-1',
        shadow: 'md',
      }}
    >
      <VStack gap={3} w="full" h="full" align="stretch">
        {channels.length > 1 || selectedChannel ? (
          <HStack w="full" justify="flex-start">
            {channels.length > 1 && (
              <NativeSelectRoot size="sm" w="fit-content">
                <NativeSelectField
                  aria-label={t('messages.channel.select')}
                  value={selectedChannelId ?? ''}
                  onChange={(event) => onChannelChange(event.target.value)}
                  items={channels.map((channel) => ({
                    value: channel.id,
                    label: t(`messages.channel.${channel.type}`),
                  }))}
                />
              </NativeSelectRoot>
            )}
            {selectedChannel && (
              <Badge size="sm" variant="subtle" colorPalette="gray">
                {t(`messages.channel.${selectedChannel.type}`)}
              </Badge>
            )}
          </HStack>
        ) : null}

        {/* Input Area */}
        <Box w="full" flex="1" position="relative">
          <HStack w="full" h="full" gap={0} align="stretch">
            {/* Editor Container */}
            <Box flex="1" position="relative">
              <LexicalComposer initialConfig={initialConfig}>
                <ExposeEditorInstancePlugin onReady={setEditor} />

                <PlainTextPlugin
                  contentEditable={
                    <ContentEditable
                      ariaLabel={t('messages.composer.ariaLabel')}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        outline: 'none',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        maxHeight: '120px',
                        overflowY: 'auto',
                        resize: 'none',
                        width: '100%',
                      }}
                    />
                  }
                  aria-placeholder={t('messages.composer.ariaLabel')}
                  placeholder={
                    <Text color="gray.500" pointerEvents="none" fontSize="md" userSelect="none">
                      {t('messages.composer.placeholder')}
                    </Text>
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
                <SendOnEnterPlugin onSubmit={handleSubmit} isLoading={sending || isLoading} />
                <OnChangePlugin onChange={handleEditorChange} />
                <HistoryPlugin />
              </LexicalComposer>
            </Box>

            {/* Submit Button Container */}
            <Flex alignItems="flex-end">
              <SubmitConversationButton
                ariaLabel={t('messages.composer.send')}
                onClick={handleSubmit}
                loading={sending || isLoading}
                disabled={isEditorEmpty || sending || isLoading || isOverLimit}
                isReady={!isEditorEmpty && !sending && !isLoading && !isOverLimit}
              />
            </Flex>
          </HStack>
        </Box>

        {/* Character Counter */}
        {charCount > 0 && (
          <Text
            fontSize="xs"
            textAlign="right"
            px={1}
            color={isOverLimit ? 'red.500' : isNearLimit ? 'orange.500' : 'gray.400'}
            fontWeight={isOverLimit ? 'semibold' : 'normal'}
          >
            {isOverLimit
              ? t('invitations.editor.characterLimit', { max: maxCharacters })
              : t('invitations.editor.characterCount', {
                  current: charCount,
                  max: maxCharacters,
                })}
          </Text>
        )}
      </VStack>
    </Box>
  );
};

export default ConversationInput;
